import { Contact } from '../components/address-book/address-book-types';
import { EnvelopeType } from './envelope';
import { ApiResponse } from './services';

export enum AddressTypeCode {
  SENDER = 'S',
  RECIPIENT = 'R',
}

export enum AddressTypes {
  RECIPIENT = 'recipient',
  SENDER = 'sender',
  DIGITAL = 'digital',
}

export enum PrefixForm {
  EMAIL_FORM = 'emailAddressForm',
  ADDRESS_FORM = 'addressForm',
}

export type AddressProps = {
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  state: string;
  zip: string;
};

export type BaseAddress = {
  first_name: string;
  last_name: string;
  display_name: string;
  company_name: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_code?: string;
  zip: string;
  country_code: string;
  address_type_code: AddressTypeCode;
  address_hash_id?: string;
};

export type AddressToSave = Omit<BaseAddress, 'address_hash_id'> & {
  skip_usps_validation: boolean;
};

export type AddressResponseData = BaseAddress & {
  address_id: string;
  created_at: string;
  last_updated_at: string;
  is_verified: boolean;
  is_valid: boolean;
};

export type SaveAddressResponse = ApiResponse<AddressResponseData>;

export type SaveAddressesResponse = ApiResponse<string>;

export type UpdateAddress = SaveAddressResponse;

export type GetAddresses = ApiResponse<AddressResponseData[] | null>;

export type Address = BaseAddress & {
  skip_usps_validation?: boolean;
  send_to?: string;
  envelope?: EnvelopeType | null;
};

// AddressData contains all the properties of an address stored in the context
export type AddressData = BaseAddress &
  Partial<AddressResponseData> & {
    address_id?: string;
    skip_usps_validation?: boolean;
    isQuickAddress?: boolean;
  };

export type AddressForm = Address & {
  address_id?: string;
  isQuickAddress?: boolean;
  is_verified?: boolean;
};

type QuickAddress = Omit<BaseAddress, 'address_type_code'> & {
  created_at?: string;
  last_updated_at?: string;
  is_verified?: boolean;
  contact_id?: string;
  account_id?: string;
  contact_type_code: string;
};

export type QuickAddressSubmitData = Omit<BaseAddress, 'address_type_code'> & {
  contact_type_code: string;
  skip_address_verification: boolean;
};

export type QuickAddressResponse = ApiResponse<QuickAddress>;

export type QuickAddresses = {
  contacts: Contact[];
  paging: {
    total_pages: number;
    total_items: number;
    next_page: number | null;
  };
};

export type QuickAddressesResponse = ApiResponse<QuickAddresses>;

export enum ErrorType {
  Incomplete = 'err_incomplete_address',
  Invalid = 'err_invalid_address',
  MissingPrimaryNumber = 'err_missing_primary_number',
  InvalidPrimaryNumber = 'err_invalid_primary_number',
  RequiredSecondaryInformationMissing = 'err_required_secondary_information_missing',
  RequiredSecondaryInformationNotRecognized = 'err_required_secondary_information_not_recognized',
  InvalidBoxNumber = 'err_invalid_box_number',
}

export enum WarningType {}

export const addressError: Record<ErrorType, boolean> = {
  [ErrorType.Invalid]: true,
  [ErrorType.Incomplete]: true,
  [ErrorType.MissingPrimaryNumber]: true,
  [ErrorType.InvalidPrimaryNumber]: true,
  [ErrorType.RequiredSecondaryInformationMissing]: true,
  [ErrorType.RequiredSecondaryInformationNotRecognized]: true,
  [ErrorType.InvalidBoxNumber]: true,
};

export type FormValidation = {
  errorType: ErrorType;
  originalAddressData?: Address;
  contactId?: string | number;
  addressType: AddressTypes;
  address: AddressProps;
  step: number;
};

export type AssetFormData = {
  front_url: string;
  inside_url: string;
};

export type RecipientResponseData = {
  url: string;
};

export type SaveAssetResponse = ApiResponse<RecipientResponseData>;

export enum BulkUploadAddressFormats {
  csv = 'text/csv',
  xlxs = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export interface AddressUpdate {
  title?: string;
  first_name: string;
  last_name: string;
  display_name: string;
  company_name: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_code: string;
  zip: string;
  country_code: string;
  skip_address_verification: boolean;
  skip_usps_validation?: boolean;
  address_type_code?: AddressTypeCode;
}

// FormFieldAddress is a type that suffies the UseFormReturn<TFieldValues> type
// when handling the form field generator (useFormFieldGenerator)
export type FormFieldAddress = {
  first_name: string;
  last_name: string;
  display_name: string;
  address_line_1: string;
  address_line_2: string;
  company_name: string;
  city: string;
  state_code: string;
  zip: string;
  isQuickAddress: boolean;
};

export type AddressRecipientUpdate = {
  first_name: string;
  last_name: string;
  display_name: string;
  company_name: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_code: string;
  zip: string;
  country_code: string;
  skip_usps_validation?: boolean;
};

export type AddressRecipientUpdateForm = AddressRecipientUpdate & {
  address_id: string;
};

export type DigitalAddress = {
  recipient_first_name: string;
  recipient_last_name: string;
  contact_info: string;
  sender_first_name: string;
  sender_last_name: string;
  sender_email: string;
  url: string;
  recipient_type_code: string;
};

export type DigitalAddressResponse = {
  recipient_id: string;
  view_date: string;
  view_notification_date: string;
  recipient_type_code: string;
  created_at: string;
  last_updated_at: string;
} & DigitalAddress;

export type GetDigitalAddress = ApiResponse<DigitalAddressResponse[] | null>;

export type SaveDigitalAddress = ApiResponse<DigitalAddressResponse>;

export type EmailAddressForm = DigitalAddress & {
  recipient_type_code: string;
};

export type LinkRecipient = Pick<
  DigitalAddress,
  'sender_first_name' | 'sender_last_name' | 'sender_email' | 'recipient_first_name' | 'recipient_last_name'
> & {
  recipient_type_code: 'L';
  contact_info: null;
};

export type DigitalRecipientRequest = Omit<EmailAddressForm, 'url'> | LinkRecipient;
